import { type AiApi, createHttpAiApi } from "./ai";
import { type AnalyticsApi, createHttpAnalyticsApi } from "./analytics";
import { type AppointmentsApi, createHttpAppointmentsApi } from "./appointments";
import { type AuditApi, createHttpAuditApi } from "./audit";
import { type ApiClient, createApiClient, operationPath } from "./client";
import { type ConversationsApi, createHttpConversationsApi } from "./conversations";
import { createHttpDevApi, type DevApi } from "./dev";
import { createHttpFollowupsApi, type FollowupsApi } from "./followups";
import { createHttpIntegrationsApi, type IntegrationsApi } from "./integrations";
import { createHttpOfferApiApi, type OfferApiApi } from "./offer-api";
import { createHttpOpportunitiesApi, type OpportunitiesApi } from "./opportunities";
import { createHttpOrganizationsApi, type OrganizationsApi } from "./organizations";
import { createHttpPromptsApi, type PromptsApi } from "./prompts";
import { createHttpPurchasesApi, type PurchasesApi } from "./purchases";
import { createHttpQueuesApi, type QueuesApi } from "./queues";
import { createEventSourceRealtime, type RealtimeSource } from "./realtime";
import { createHttpSearchApi, type SearchApi } from "./search";
import { createHttpSessionApi, type SessionApi } from "./session";
import { createHttpStaffApi, type StaffApi } from "./staff";
import { createHttpStoresApi, type StoresApi } from "./stores";
import { createHttpSystemApi, type SystemApi } from "./system";
import { createHttpUsersApi, type UsersApi } from "./users";

/** Everything the UI can ask of the platform, grouped by domain. */
export interface AdminApi {
  session: SessionApi;
  system: SystemApi;
  organizations: OrganizationsApi;
  stores: StoresApi;
  users: UsersApi;
  opportunities: OpportunitiesApi;
  conversations: ConversationsApi;
  appointments: AppointmentsApi;
  ai: AiApi;
  queues: QueuesApi;
  integrations: IntegrationsApi;
  offerApi: OfferApiApi;
  purchases: PurchasesApi;
  analytics: AnalyticsApi;
  audit: AuditApi;
  search: SearchApi;
  staff: StaffApi;
  dev: DevApi;
  prompts: PromptsApi;
  followups: FollowupsApi;
}

export interface Runtime {
  api: AdminApi;
  realtime: RealtimeSource;
  mode: "http" | "mock";
  mockControls?: { emitSample: () => void };
}

export const USE_MOCK_API = import.meta.env.VITE_USE_MOCK_API === "true";
export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");

export function createHttpAdminApi(client: ApiClient = createApiClient(API_BASE_URL)): AdminApi {
  return {
    session: createHttpSessionApi(client),
    system: createHttpSystemApi(client),
    organizations: createHttpOrganizationsApi(client),
    stores: createHttpStoresApi(client),
    users: createHttpUsersApi(client),
    opportunities: createHttpOpportunitiesApi(client),
    conversations: createHttpConversationsApi(client),
    appointments: createHttpAppointmentsApi(client),
    ai: createHttpAiApi(client),
    queues: createHttpQueuesApi(client),
    integrations: createHttpIntegrationsApi(client),
    offerApi: createHttpOfferApiApi(client),
    purchases: createHttpPurchasesApi(client),
    analytics: createHttpAnalyticsApi(client),
    audit: createHttpAuditApi(client),
    search: createHttpSearchApi(client),
    staff: createHttpStaffApi(client),
    dev: createHttpDevApi(client),
    prompts: createHttpPromptsApi(client),
    followups: createHttpFollowupsApi(client),
  };
}

let runtime: Runtime | undefined;

/**
 * Picks the runtime once at startup. Mock mode (VITE_USE_MOCK_API=true) is for local visual
 * development only; its module is imported lazily so production builds contain no mock data.
 */
export async function initRuntime(): Promise<Runtime> {
  if (runtime) return runtime;
  if (USE_MOCK_API) {
    const { createMockRuntime } = await import("./mock");
    runtime = createMockRuntime();
  } else {
    runtime = {
      api: createHttpAdminApi(),
      // The admin SSE stream (contract: streamAdminEvents), with cookies and backoff reconnects.
      realtime: createEventSourceRealtime(`${API_BASE_URL}${operationPath("streamAdminEvents")}`),
      mode: "http",
    };
  }
  return runtime;
}

export function setRuntime(next: Runtime) {
  runtime = next;
}

export function getRuntime(): Runtime {
  if (!runtime) throw new Error("Runtime not initialized");
  return runtime;
}

/** The domain API used by query and mutation hooks. Components never import this directly. */
export const api = new Proxy({} as AdminApi, {
  get: (_t, key: keyof AdminApi) => getRuntime().api[key],
});
